import Decimal from 'decimal.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import plugbookClient, { PlugbookRecord } from '../clients/PlugbookClient'
import { solanaClient } from '../clients/SolanaClient'
import TatumClient from '../clients/TatumClient'
import { ETH_CHAIN_ID, UTXO_CHAINS } from '../constants/business'
import { ICP_LEDGER, TCYCLES_CANISTER_ID, XTC_CANISTER_ID } from '../constants/canisterIds'
import { BTC_ID, ICP_ICP_ID, SOL_ID } from '../constants/tokenIds'
import { BTCNFT, Chain, Contact, ContactSection, NFT, PlugToken } from '../interfaces/common'
import { UTXO, UTXOChain } from '../interfaces/walletActions'
import { logger } from '../logger'
import { TokenBalance } from '../providers/BalanceProvider'
import {
  useContacts,
  useLocales,
  useUserTokens,
  useWalletBTCAddresses,
  useWalletProperties,
} from '../state'
import addressMatcher, { AddressType } from '../utils/addressMatcher'
import { maxBigInt } from '../utils/bigint'
import { formatMoney, formatNumber } from '../utils/formatNumber'
import { GasCalculator } from '../utils/GasCalculator'
import { getFeeToken } from '../utils/getFeeToken'
import { decimalsToE8s, e8sToDecimals } from '../utils/validationHelper'
import useBalances from './useBalances'
import useConfig from './useConfig'
import useDebouncedState from './useDebouncedState'
import useFindTokens from './useFindTokens'
import { useMaxUtxoSendable, useTransferFeeEstimate } from './useGas'
import useMintCycles from './useMintCycles'
import useTopup from './useTopup'
import useWallet from './useWallet'

const isUtxoChain = (chain: Chain | undefined): chain is UTXOChain => {
  return chain !== undefined && UTXO_CHAINS.includes(chain as UTXOChain)
}

export enum SendMode {
  Send = 'send',
  CycleTopup = 'cycleTopup',
}

interface Props {
  walletPrincipals: string[]
  selectedTokenId?: string
  selectedNft?: NFT
  mode?: SendMode
}

export interface Fee {
  token: Pick<PlugToken, 'id' | 'symbol' | 'decimals'>
  amount?: bigint
  amountHuman?: string
}

const isValidChainAddress = (token: PlugToken, addressType: AddressType) => {
  if (token.chain === Chain.SOL) {
    return addressType === AddressType.Sol
  } else if (token.chain === Chain.BTC) {
    return addressType === AddressType.Btc
  } else if (token.chain === Chain.DOGE) {
    return addressType === AddressType.Doge
  } else if (token.chain === Chain.ICP) {
    return (
      addressType === AddressType.PrincipalId ||
      (token.id === ICP_ICP_ID && addressType === AddressType.AccountId)
    )
  } else if (token.chain === Chain.ODIN) {
    return addressType === AddressType.PrincipalId
  } else if (token.chain === Chain.HYPERCORE) {
    return addressType === AddressType.Eth
  } else if (addressType !== AddressType.Invalid) {
    return addressType === AddressType.Eth
  } else {
    return false
  }
}

const TOPUP_TOKENS = [ICP_LEDGER, TCYCLES_CANISTER_ID, XTC_CANISTER_ID]

const useSend = ({ walletPrincipals, selectedTokenId, selectedNft, mode }: Props) => {
  const { t } = useTranslation()

  const [summaryVisible, setSummaryVisible] = useState(false)
  const [debouncedToAddress, setToAddress, toAddress] = useDebouncedState('', 300)
  const [tokenId, setTokenId] = useState<string>()
  const [nft, setNft] = useState<NFT>()

  const [amount, setAmount] = useState<string>('')
  const [amountCurrency, setAmountCurrency] = useState<string>('')
  const [selectedMode, setSelectedMode] = useState('token')
  const [contactModalVisible, setContactModalVisible] = useState(false)
  const [sendError, setSendError] = useState<string>()
  const [isFindingTokenBalances, setIsFindingTokenBalances] = useState(false)
  const [xtcSendMode, setXtcSendMode] = useState<string>('')

  const [contact, setContact] = useState<Contact>()
  const [plugbookContacts, setPlugbookContacts] = useState<PlugbookRecord[]>([])

  const { balances, findBalance, refreshBalance, walletNfts } = useBalances()
  const token = findBalance({ id: tokenId })

  const { wallet } = useWallet()
  const { nfts, tokens } = useConfig()
  const { contacts } = useContacts()
  const {
    utxoFee,
    utxoFeeError,
    ethFee,
    solFee,
    isLoading: isLoadingFeeData,
  } = useTransferFeeEstimate(token?.token, amount, toAddress)
  const utxoMaxAmount = useMaxUtxoSendable(token?.token)
  const { savedTokens } = useUserTokens()
  const { findTokens } = useFindTokens()
  const { userLocale } = useLocales()
  const { savedCanisters } = useTopup()
  const { setInputAmount: setCyclesInputICP, outAmount: cyclesOutAmount } = useMintCycles()

  const assetChain: Chain | undefined = useMemo(() => {
    if (token?.token.chain) {
      return token.token.chain
    }

    if (nft) {
      return nft.standard === 'ordinal' ? Chain.BTC : Chain.ICP
    }
  }, [token, nft])

  const e8sAmount = useMemo(() => {
    return token ? decimalsToE8s(amount || '', token?.token.decimals) : '0'
  }, [amount, token])

  const [utxos, setUtxos] = useState<UTXO[]>([])

  const [wallets, setWallets] = useState<Contact[]>([])
  const {
    walletNames,
    walletPlugbookRecords,
    walletEthAddresses,
    walletSolAddresses,
    walletDogeAddresses,
  } = useWalletProperties()
  const { walletBTCAddresses } = useWalletBTCAddresses()

  useEffect(() => {
    const loadWallets = async () => {
      try {
        setWallets(
          walletPrincipals
            .filter((p) => p.toString() !== wallet.principalId)
            .map((principal) => ({
              id: principal,
              avatar: walletPlugbookRecords[principal].profilePic || '👽',
              name: walletNames[principal],
              addresses: [
                { chain: Chain.ICP, address: principal },
                { chain: ETH_CHAIN_ID.toString(), address: walletEthAddresses[principal] },
                { chain: Chain.SOL, address: walletSolAddresses[principal] },
                { chain: Chain.BTC, address: walletBTCAddresses[principal] },
                { chain: Chain.DOGE, address: walletDogeAddresses[principal] },
              ].filter(({ address }) => !!address),
            }))
        )
      } catch (e) {
        logger.error('Failed to set wallets', { originalError: e })
      }
    }

    loadWallets()
  }, [walletPrincipals])

  useEffect(() => {
    if (mode === SendMode.CycleTopup && token?.token.address === ICP_LEDGER) {
      setCyclesInputICP(amount)
    } else {
      setCyclesInputICP('')
    }
  }, [mode, setCyclesInputICP, amount, token])

  useEffect(() => {
    setTokenId(selectedTokenId)
  }, [selectedTokenId])

  useEffect(() => {
    setNft(selectedNft)
  }, [selectedNft])

  useEffect(() => {
    const token = tokens.find((t) => t.id === tokenId)
    token && refreshBalance(token)
  }, [tokenId, tokens])

  const utxoAddress = useMemo(() => {
    if (assetChain === Chain.BTC) return wallet.btcAddress
    if (assetChain === Chain.DOGE) return wallet.dogeAddress
    return undefined
  }, [assetChain, wallet.btcAddress, wallet.dogeAddress])

  useEffect(() => {
    if (!utxoAddress || !isUtxoChain(assetChain)) return

    TatumClient.getUTXOs(utxoAddress, assetChain).then(setUtxos).catch(logger.debug)
  }, [utxoAddress, assetChain])

  const receiverAddress = useMemo(() => {
    if (toAddress) return toAddress
    if (contact) {
      if (!token && !selectedNft) return contact.addresses[0].address

      if (nft) {
        if (nft.standard === 'ordinal') {
          return contact.addresses.find(({ chain }) => chain === Chain.BTC)?.address
        } else {
          return contact.addresses.find(
            (address) => addressMatcher(address.address) === AddressType.PrincipalId
          )?.address
        }
      }

      if (token) {
        let normalizedChain: string = token.token.chain
        if (token.token.evmChain) normalizedChain = ETH_CHAIN_ID.toString()
        if (token.token.chain === Chain.ODIN) normalizedChain = Chain.ICP
        if (token.token.chain === Chain.HYPERCORE) normalizedChain = ETH_CHAIN_ID.toString()

        return contact.addresses.find(({ chain }) => chain === normalizedChain)?.address
      }
    }
  }, [toAddress, selectedNft, token, contact])

  const filterContact = ({ name, addresses }: Contact) => {
    const addressTypes = addresses.map((address) => addressMatcher(address.address))

    if (
      token &&
      !addressTypes.some((addressType) => isValidChainAddress(token.token, addressType))
    ) {
      return false
    } else if (toAddress) {
      return (
        (name || '').toLowerCase().includes(toAddress.toLowerCase()) ||
        addresses.map((address) => address.address.toLowerCase()).includes(toAddress.toLowerCase())
      )
    } else {
      return true
    }
  }

  const utxosToUse: UTXO[] = useMemo(() => {
    if (!isUtxoChain(assetChain) || !utxoFee) return []

    // Ordinals only apply to BTC
    const ordinals =
      assetChain === Chain.BTC
        ? walletNfts.flatMap((collection) =>
            collection.nfts.filter((nft) => nft.standard === 'ordinal')
          )
        : []

    return GasCalculator.utxosForAmount(
      utxos,
      ordinals as BTCNFT[],
      e8sAmount ? BigInt(e8sAmount) + utxoFee : utxoFee,
      assetChain === Chain.BTC ? (nft as BTCNFT) : undefined
    )
  }, [token, utxos, utxoFee, walletNfts, assetChain, e8sAmount, nft])

  const fee: Fee | undefined = useMemo(() => {
    if (nft) {
      if (assetChain === Chain.ICP) {
        return {
          token: { symbol: 'ICP', id: ICP_ICP_ID, decimals: 8 },
          amount: 0n,
        }
      } else if (assetChain === Chain.BTC) {
        return {
          token: { symbol: 'BTC', id: BTC_ID, decimals: 8 },
          amount: utxoFee,
        }
      }
    } else if (token) {
      const feeToken = getFeeToken(token.token, tokens)
      let feeAmount: bigint | undefined

      if (token.token.evmChain) {
        feeAmount = ethFee ? BigInt(ethFee) : undefined
      } else if (isUtxoChain(token.token.chain)) {
        feeAmount = utxoFee
      } else if (token.token.chain === Chain.SOL) {
        feeAmount = solFee
      } else {
        feeAmount = token.token.fee
      }

      return { token: feeToken, amount: feeAmount }
    }
  }, [token, nft, ethFee, utxosToUse, utxoFee, assetChain])

  let maxAmount
  if (isUtxoChain(token?.token.chain)) {
    maxAmount = utxoMaxAmount
  } else if (token?.token.id === fee?.token.id) {
    maxAmount = maxBigInt((token?.balance || 0n) - (fee?.amount || 0n), 0n)
  } else {
    maxAmount = token?.balance
  }

  const addressType = addressMatcher(receiverAddress || '')
  const addressMatched = token
    ? isValidChainAddress(token.token, addressType)
    : addressType !== AddressType.Invalid

  useEffect(() => {
    if (token?.tokenPrice && selectedMode !== 'currency') {
      const exchangeRate = token.tokenPrice

      if (amount) {
        const currencyAmount = Decimal(amount).mul(exchangeRate).toNumber()
        setAmountCurrency(currencyAmount.toFixed(2))
      }
    }
  }, [token, amount, selectedMode])

  let feeAmountCurrency
  if (fee?.amount && fee.token) {
    const feeToken = findBalance({ id: fee.token.id })

    if (feeToken?.tokenPrice) {
      feeAmountCurrency = formatMoney(
        e8sToDecimals(fee.amount, feeToken.token.decimals) * feeToken.tokenPrice
      )
    }
  }

  useEffect(() => {
    if (!amountCurrency || !token?.tokenPrice || selectedMode === 'token') return

    const exchangeRate = Decimal(token.tokenPrice)
      .mul(userLocale.currency.usdExchangeRate)
      .toNumber()
    const newTokenAmount = parseFloat(amountCurrency) / exchangeRate

    setAmount(formatNumber(newTokenAmount, undefined, { useGrouping: false })!)
  }, [token, amountCurrency, selectedMode])

  useEffect(() => {
    if (!debouncedToAddress) return

    plugbookClient
      .autocomplete(debouncedToAddress.replace('@', ''))
      .then(setPlugbookContacts)
      .catch(logger.error)
  }, [debouncedToAddress])

  const error = useMemo(() => {
    const parsedAmount = token ? BigInt(e8sAmount) : 0n

    if (
      [
        wallet.btcAddress,
        wallet.solAddress,
        wallet.ethAddress,
        wallet.principalId,
        wallet.accountId,
        wallet.dogeAddress,
      ].includes(toAddress)
    ) {
      return t('send.errors.ownWallet')
    }

    if (!nft && !parsedAmount) return t('send.errors.noAmount')

    if (token?.token.standard === 'XTC' && mode === SendMode.Send && !xtcSendMode) {
      return t('send.errors.xtcNoMode')
    }

    if (token && parsedAmount > token.balance) {
      return t('send.errors.lowBalance', { token: token.token.symbol })
    }

    if (token && fee?.amount) {
      if (fee.token.id !== token.token.id) {
        const feeTokenBalance = findBalance({ id: fee.token.id })?.balance || 0n

        if (fee.amount > feeTokenBalance) {
          return t('send.errors.notEnoughForFee', { token: fee.token.symbol })
        }
      } else if (parsedAmount + fee.amount > token.balance) {
        return t('send.errors.notEnoughForFee', { token: token.token.symbol })
      }
    }
    if (isLoadingFeeData) {
      return t('send.errors.calculatingFee')
    } else if (isUtxoChain(assetChain) && !utxoFee) {
      const symbol = token?.token.symbol || assetChain.toUpperCase()
      if (utxoFeeError?.kind === 'networkUnavailable') {
        return t('send.errors.btcNetworkUnavailable', { token: symbol })
      }
      if (utxoFeeError?.kind === 'inscriptionsUnavailable') {
        return t('send.errors.inscriptionsUnavailable')
      }
      if (utxoFeeError?.kind === 'insufficientSpendable' && utxoFeeError.locked > 0n) {
        return t('send.errors.spendableAfterInscriptions', { token: symbol })
      }
      return t('send.errors.notEnoughForFee', { token: symbol })
    } else if (typeof fee?.amount == 'undefined') {
      return t('send.errors.calculatingFee')
    }
  }, [
    e8sAmount,
    token,
    t,
    nft,
    xtcSendMode,
    addressType,
    utxoFee,
    utxoFeeError,
    assetChain,
    fee,
    isLoadingFeeData,
  ])

  const hasSelectedAsset = useMemo(() => {
    return !!token || !!nft
  }, [token, nft])

  const balancesForSelect = useMemo(() => {
    const relevantBalances = balances.filter((balance) =>
      savedTokens[wallet.principalId]?.includes(balance.token.id)
    )

    if (mode === SendMode.CycleTopup) {
      return relevantBalances.filter((balance) => TOPUP_TOKENS.includes(balance.token.address!))
    }

    return relevantBalances.filter((balance) => {
      if (contact) {
        const addressTypes = contact.addresses.map((address) => addressMatcher(address.address))

        return addressTypes.some((type) => isValidChainAddress(balance.token, type))
      }

      return isValidChainAddress(balance.token, addressType)
    })
  }, [balances, savedTokens, addressType, contact])

  const canSendNft = useMemo(() => {
    return (
      (addressType === AddressType.PrincipalId || addressType === AddressType.Btc) &&
      !hasSelectedAsset &&
      mode === SendMode.Send
    )
  }, [addressType, mode, hasSelectedAsset])

  const showXTCSendMode = useMemo(() => {
    return addressMatched && token?.token.standard === 'XTC' && mode === SendMode.Send
  }, [addressMatched, token, mode])

  const contactSections: ContactSection[] = useMemo(() => {
    let sections: ContactSection[] = []

    if (mode === SendMode.Send) {
      sections.push({
        kind: 'wallets' as ContactSection['kind'],
        contacts: wallets || [],
      })

      sections.push({
        kind: 'contacts' as ContactSection['kind'],
        contacts: contacts.filter((c) => c.addresses.length > 0),
      })

      sections.push({
        kind: 'plugbook',
        contacts: plugbookContacts
          .filter((pbc) => pbc.name !== wallet.plugbook?.name)
          .map(plugbookClient.plugbookRecordToContact),
      })
    }

    if (mode === SendMode.CycleTopup) {
      sections.push({
        kind: 'canisters' as ContactSection['kind'],
        contacts: savedCanisters.map((canister) => ({
          id: canister.id.toString(),
          avatar: '⛽️',
          name: canister.name,
          addresses: [{ chain: Chain.ICP, address: canister.id.toString() }],
        })),
      })
    }

    sections.forEach((section) => (section.contacts = section.contacts.filter(filterContact)))

    return sections
  }, [contacts, wallets, mode, savedCanisters, assetChain, toAddress, plugbookContacts])

  const matchedContact = useMemo(() => {
    return contactSections
      .map((s) => s.contacts)
      .flat()
      .find((contact) =>
        contact?.addresses.map((address) => address.address).includes(receiverAddress || '')
      )
  }, [contactSections, receiverAddress])

  const receiverChain = useMemo(() => {
    if (addressType === AddressType.Btc) {
      return Chain.BTC
    } else if (addressType === AddressType.PrincipalId) {
      return Chain.ICP
    }
  }, [addressType])

  const onError = useCallback(
    (error: string) => {
      setSummaryVisible(false)
      setSendError(error)
    },
    [setSendError, setSummaryVisible]
  )

  const validateAndShowSummary = async () => {
    if (assetChain === 'sol' && wallet.solAddress && token && token.token.id === SOL_ID) {
      const totalCost = BigInt(e8sAmount) + (fee?.amount || 0n)
      const rent = await solanaClient.calculateAccountRent(wallet.solAddress)

      if (token.balance - totalCost !== 0n && token.balance < totalCost + rent) {
        return setSendError(t('send.errors.notEnoughSolForRent', { rentFee: rent.toString() }))
      }
    }

    setSummaryVisible(true)
  }

  return {
    addressMatched,
    hasSelectedAsset,
    toAddress,
    setToAddress: (address: string) => {
      setContact(undefined)
      setToAddress(address)
    },
    setContactModalVisible,
    addressType,
    nft,
    nfts,
    token,
    balancesForSelect,
    onTokenSelect: (token: TokenBalance | undefined) => setTokenId(token?.token.id),
    isFindingTokenBalances,
    setIsFindingTokenBalances,
    findTokens,
    setNft,
    amount,
    setAmount,
    maxAmount,
    selectedMode,
    setSelectedMode,
    amountCurrency,
    feeAmountCurrency,
    setAmountCurrency,
    userLocale,
    xtcSendMode,
    setXtcSendMode,
    fee,
    validateAndShowSummary,
    error,
    summaryVisible,
    setSummaryVisible,
    e8sAmount,
    utxosToUse,
    contactModalVisible,
    sendError,
    setSendError,
    onError,
    wallet,
    walletNfts,
    canSendNft,
    showXTCSendMode,
    cyclesOutAmount,
    contactSections,
    contacts,
    receiverChain,
    setContact: (contact: Contact) => {
      setContact(contact)
      setToAddress('')
    },
    matchedContact,
    receiverAddress: receiverAddress || '',
  }
}

export default useSend
